import { TFunction } from "i18next";

/**
 * How a route's name is written in the Routing and Difficulty tables.
 *
 * Sub-agent routes are the reason this is shared rather than inlined in each panel: their label is
 * derived from the *parent* route's label, and the two tables must not drift on how that reads.
 *
 * `inheritsFrom` comes from the server. Do not re-derive it from the `subagent_` prefix here —
 * `subagent_decomposer` carries that prefix but is a platform route configured in its own right,
 * and the server already excludes it.
 */
export const routeDisplayName = (
  t: TFunction,
  route: { agentRoute: string; inheritsFrom: string | null },
): string => {
  const own = t(`app.aiGateway.routes.${route.agentRoute}`, {
    defaultValue: route.agentRoute,
  });

  // A tenant's agent names are arbitrary, so there is no translation key per sub-agent route.
  // Interpolating the parent's label is what lets "Sub-agents of Support" translate at all.
  return route.inheritsFrom
    ? t("app.aiGateway.routes.subagentOf", {
        parent: t(`app.aiGateway.routes.${route.inheritsFrom}`, {
          defaultValue: route.inheritsFrom,
        }),
      })
    : own;
};

/**
 * The "this row is following its parent" line under a sub-agent route, or `null` when the route
 * has no parent and the caller should render nothing.
 */
export const routeInheritanceHint = (
  t: TFunction,
  route: { inheritsFrom: string | null },
): string | null =>
  route.inheritsFrom
    ? t("app.aiGateway.routes.inheritsHint", {
        parent: t(`app.aiGateway.routes.${route.inheritsFrom}`, {
          defaultValue: route.inheritsFrom,
        }),
      })
    : null;
